import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

// sessionStorage key for one-per-session click tracking
const CLICK_TRACKED_KEY = 'evee_referral_click_tracked';

/**
 * Hook to track referral token clicks
 * Updates click_count and last_clicked_at in referral_tokens table
 * Only tracks once per session to prevent duplicate clicks
 */
export function useReferralClickTracking(tokenFromUrl?: string) {
  const trackingInProgress = useRef<Set<string>>(new Set());

  useEffect(() => {
    const trackClick = async () => {
      if (!tokenFromUrl) return;

      // Check if we've already tracked this token in this session
      const trackedTokens = getTrackedClicks();
      if (trackedTokens.includes(tokenFromUrl)) {
        console.log('⏭️ [useReferralClickTracking] Click already tracked this session:', tokenFromUrl.substring(0, 8) + '...');
        return;
      }

      // Prevent concurrent RPC calls for the same token
      if (trackingInProgress.current.has(tokenFromUrl)) {
        console.log('⏭️ [useReferralClickTracking] Tracking already in progress:', tokenFromUrl.substring(0, 8) + '...');
        return;
      }

      // Mark as tracked BEFORE making the RPC call to prevent race conditions
      trackingInProgress.current.add(tokenFromUrl);
      markClickAsTracked(tokenFromUrl);

      try {
        // Call the database function to increment click count
        const { error } = await supabase.rpc('increment_referral_click', {
          token_param: tokenFromUrl,
        });

        if (error) {
          console.error('Failed to track referral click:', error);
          // Rollback on error
          const tracked = getTrackedClicks();
          const index = tracked.indexOf(tokenFromUrl);
          if (index > -1) {
            tracked.splice(index, 1);
            sessionStorage.setItem(CLICK_TRACKED_KEY, JSON.stringify(tracked));
          }
        } else {
          console.log('✅ Referral click tracked');
        }
      } catch (err) {
        console.error('Error tracking referral click:', err);
        // Rollback on error
        const tracked = getTrackedClicks();
        const index = tracked.indexOf(tokenFromUrl);
        if (index > -1) {
          tracked.splice(index, 1);
          sessionStorage.setItem(CLICK_TRACKED_KEY, JSON.stringify(tracked));
        }
      } finally {
        trackingInProgress.current.delete(tokenFromUrl);
      }
    };

    trackClick();
  }, [tokenFromUrl]);
}

// Helper functions for session-based click deduplication
function getTrackedClicks(): string[] {
  try {
    const stored = sessionStorage.getItem(CLICK_TRACKED_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

function markClickAsTracked(token: string) {
  try {
    const tracked = getTrackedClicks();
    if (!tracked.includes(token)) {
      tracked.push(token);
      sessionStorage.setItem(CLICK_TRACKED_KEY, JSON.stringify(tracked));
    }
  } catch (error) {
    console.error('Failed to mark click as tracked:', error);
  }
}
